import { createSlice } from "@reduxjs/toolkit";
import { factory } from "../../api/apiFactory";
import { showNotification } from "./notificationSlice";

const itemRequestsApi = factory.get("itemRequests");

const initialState = {
  itemRequests: {
    loading: false,
    data: [],
    total: 0,
    filters: {
      page: 0,
      skip: 0,
      take: 15,
      search: "",
      number: "",
      status: ["in_progress", "open"],
      tags_id: [],
      department_id: null,
      related_department_id: null,
      request_type: null,
      start_date: null,
      end_date: null,
      start_update_date: null,
      end_update_date: null,
    },
    dialog: false,
    isFilterCleared: false,
  },
  itemRequest: {
    form: {
      title: "",
      description: "",
      project_id: null,
      current_department_id: null,
      tag_id: null,
      is_private: false,
      priority: "normal",
      request_type: "mr",
      related_to_ticket_id: null,
      ats_id: null,
      client_id: null,
      sub_category_id: null,
      attachment: [],
      ticketItems: [],
    },
    dialog: false,
    loading: false,
    errors: {},
  },
  itemRequest_details: {
    data: {
      id: "",
      number: "",
      title: "",
      description: "",
      status: "",
      mr_status: null,
      request_type: null,
      total_estimated_value: null,
      priority: "",
      is_private: false,
      is_created_by_client: false,
      created_by_name: null,
      department: null,
      current_department_id: null,
      current_department_name: "",
      open_by_department: null,
      project: null,
      category: null,
      sub_category: null,
      related_tickets: [],
      items: [],
      replys: [],
      attachments: [],
      created_at: "",
      tag: null,
    },
    loading: undefined,
  },
  reply: {
    form: {
      ticket_id: "",
      tag_id: null,
      comment: "",
      is_private: true,
      current_department_id: null,
      status: "in_progress",
      attachment: [],
      assign_user: [],
    },
    loading: false,
    dialog: false,
    errors: {},
  },
  mrAction: {
    loading: false,
  },
  stockCheck: {
    loading: false,
    dialog: false,
  },
  addItemsState: {
    replyItems: [],
    loading: false,
  },
};

export const itemRequestsSlice = createSlice({
  name: "itemRequests",
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state[action.payload].loading = !state[action.payload].loading;
    },
    setItemRequestDetailLoading: (state, action) => {
      state.itemRequest_details.loading = action.payload;
    },
    setItemRequests: (state, action) => {
      state.itemRequests.data = action.payload.data.data;
      state.itemRequests.total = action.payload.data.total;
    },
    setFilters: (state, { payload }) => {
      state.itemRequests.filters = {
        ...state.itemRequests.filters,
        ...payload,
      };
    },
    resetFilters: (state) => {
      state.itemRequests.filters = initialState.itemRequests.filters;
    },
    setFilterDialog: (state) => {
      state.itemRequests.dialog = !state.itemRequests.dialog;
    },
    setIsFilterCleared: (state, { payload }) => {
      state.itemRequests.isFilterCleared = payload;
    },
    setDialog: (state, { payload }) => {
      if (payload?.type === "related" && payload?.related_to_ticket_id) {
        state.itemRequest.form.related_to_ticket_id = payload.related_to_ticket_id;
      } else {
        state.itemRequest.form = initialState.itemRequest.form;
      }
      state.itemRequest.dialog = !state.itemRequest.dialog;
    },
    resetForm: (state) => {
      state.itemRequest.form = initialState.itemRequest.form;
    },
    setItemRequestDetails: (state, { payload }) => {
      state.itemRequest_details.data = { ...payload };
    },
    setReplyItems: (state, action) => {
      state.addItemsState.replyItems = action.payload;
    },
    resetReplyItems: (state) => {
      state.addItemsState.replyItems = initialState.addItemsState.replyItems;
    },
    setStockCheckDialog: (state) => {
      state.stockCheck.dialog = !state.stockCheck.dialog;
    },
  },
});

export const {
  setLoading,
  setItemRequestDetailLoading,
  setItemRequests,
  setFilters,
  resetFilters,
  setFilterDialog,
  setIsFilterCleared,
  setDialog,
  resetForm,
  setItemRequestDetails,
  setReplyItems,
  resetReplyItems,
  setStockCheckDialog,
} = itemRequestsSlice.actions;

export default itemRequestsSlice.reducer;

// Thunks
const index = () => async (dispatch, getState) => {
  try {
    const filters = getState().itemRequests.itemRequests.filters;
    const filters_ = {
      ...filters,
      department_id: filters.department_id?.id || null,
      related_department_id: filters.related_department_id?.id || null,
      status: JSON.stringify(filters.status) || null,
      tags_id: JSON.stringify(filters.tags_id?.map((tag) => tag.id)) || null,
    };
    dispatch(setLoading("itemRequests"));
    const res = await itemRequestsApi.index(filters_);
    dispatch(setItemRequests(res));
    dispatch(setLoading("itemRequests"));
  } catch (err) {
    dispatch(setLoading("itemRequests"));
    dispatch(
      showNotification({
        type: "error",
        message: err.response?.data?.errors?.[0] || "Failed to fetch requests",
      })
    );
    throw new Error(err);
  }
};

const getById = (id) => async (dispatch) => {
  try {
    dispatch(setItemRequestDetailLoading(true));
    const res = await itemRequestsApi.showById(id);
    dispatch(setItemRequestDetails(res.data.data[0]));
    dispatch(setItemRequestDetailLoading(false));
  } catch (err) {
    dispatch(setItemRequestDetailLoading(false));
    dispatch(
      showNotification({
        type: "error",
        message: err.response?.data?.errors?.[0] || "Failed to fetch request",
      })
    );
    throw new Error(err);
  }
};

const create = (data, navigate) => async (dispatch) => {
  try {
    dispatch(setLoading("itemRequest"));
    const formdata = new FormData();

    formdata.append("title", data.title);
    formdata.append("description", data.description);
    formdata.append("request_type", data.request_type || "mr");

    if (data.project_id?.id) {
      formdata.append("project_id", data.project_id.id);
    }
    if (data.current_department_id?.id) {
      formdata.append("current_department_id", data.current_department_id.id);
    }
    if (data.tag_id?.id) {
      formdata.append("tag_id", data.tag_id.id);
    }
    if (data.priority) {
      formdata.append("priority", data.priority);
    }
    if (data.is_private !== undefined) {
      formdata.append("is_private", data.is_private);
    }
    if (data.related_to_ticket_id) {
      formdata.append("related_to_ticket_id", data.related_to_ticket_id);
    }
    if (data.client_id?.id) {
      formdata.append("client_id", data.client_id.id);
    }
    if (data.sub_category_id?.id) {
      formdata.append("sub_category_id", data.sub_category_id.id);
    }
    if (data.ats_id?.id) {
      formdata.append("ats_id", data.ats_id.id);
    }
    if (data.ticketItems?.length > 0) {
      data.ticketItems.forEach((item, index) => {
        formdata.append(`items[${index}][item_id]`, item.item_id);
        formdata.append(`items[${index}][quantity]`, item.quantity);
      });
    }
    if (data.attachment?.length > 0) {
      data.attachment.forEach((item, index) => {
        formdata.append(`attachments[${index}]`, item);
      });
    }

    const response = await itemRequestsApi.create(formdata);
    dispatch(setLoading("itemRequest"));
    dispatch(
      showNotification({
        type: "success",
        message: "Item request created successfully",
      })
    );
    dispatch(setDialog());
    dispatch(resetForm());
    const newId = response.data?.data?.[0]?.id;
    if (newId && navigate) {
      navigate(`/item-requests/${newId}`);
    } else {
      dispatch(index());
    }
  } catch (err) {
    dispatch(setLoading("itemRequest"));
    dispatch(
      showNotification({
        type: "error",
        message: err.response?.data?.errors?.[0] || "Failed to create request",
      })
    );
  }
};

const remove = (id) => async (dispatch) => {
  try {
    const ticketsApi = factory.get("tickets");
    await ticketsApi.delete(id);
    dispatch(
      showNotification({
        type: "success",
        message: "Item request deleted successfully",
      })
    );
    dispatch(index());
  } catch (err) {
    dispatch(
      showNotification({
        type: "error",
        message: err.response?.data?.errors?.[0] || "Failed to delete request",
      })
    );
    throw new Error(err);
  }
};

const reply = (data) => async (dispatch, getState) => {
  try {
    const { id, status } = getState().itemRequests.itemRequest_details.data;
    const { department_id } = getState().profile.user.info;
    dispatch(setLoading("reply"));
    const formdata = new FormData();
    formdata.append("ticket_id", data.ticket_id);
    if (data.tag_id?.id) {
      formdata.append("tag_id", data.tag_id.id);
    }
    formdata.append("status", status === "closed" ? "re_open" : data.status);
    formdata.append("comment", data.comment);
    formdata.append(
      "current_department_id",
      data.current_department_id ? data.current_department_id?.id : department_id
    );
    formdata.append("is_private", data.is_private);
    if (data.attachment?.length > 0) {
      data.attachment.forEach((item, index) => {
        formdata.append(`attachment[${index}]`, item);
      });
    }
    if (data.replyItems?.length > 0) {
      data.replyItems.forEach((item, index) => {
        formdata.append(`items[${index}][item_id]`, item.item_id);
        formdata.append(`items[${index}][quantity]`, item.quantity);
      });
    }
    if (data.assign_user?.length > 0) {
      const assign_user = data.assign_user.map((item) => item.id);
      formdata.append(`assign_user`, JSON.stringify(assign_user));
    }
    await itemRequestsApi.reply(formdata);
    dispatch(setLoading("reply"));
    dispatch(resetReplyItems());
    dispatch(
      showNotification({
        type: "success",
        message: "Reply created successfully",
      })
    );
    dispatch(getById(id));
  } catch (err) {
    dispatch(setLoading("reply"));
    dispatch(
      showNotification({
        type: "error",
        message: err?.response?.data?.errors?.[0] || "Failed to add reply",
      })
    );
  }
};

const removeReply = (id) => async (dispatch, getState) => {
  try {
    const ticketId = getState().itemRequests.itemRequest_details.data.id;
    await itemRequestsApi.deleteReply(id);
    dispatch(
      showNotification({
        type: "success",
        message: "Reply deleted successfully",
      })
    );
    dispatch(getById(ticketId));
  } catch (err) {
    dispatch(
      showNotification({
        type: "error",
        message: err.response?.data?.errors?.[0] || "Failed to delete reply",
      })
    );
  }
};

const changeReplyPrivacy = (id, status) => async (dispatch, getState) => {
  try {
    const ticketId = getState().itemRequests.itemRequest_details.data.id;
    await itemRequestsApi.changeReplyPrivacy(id, status);
    dispatch(
      showNotification({
        type: "success",
        message: "Reply privacy changed successfully",
      })
    );
    dispatch(getById(ticketId));
  } catch (err) {
    dispatch(
      showNotification({
        type: "error",
        message: err.response?.data?.errors?.[0] || "Failed to change privacy",
      })
    );
  }
};

const mrAction = (ticketId, data) => async (dispatch) => {
  try {
    dispatch(setLoading("mrAction"));
    const payload = {
      status: data.status,
    };
    if (data.comment) {
      payload.comment = data.comment;
    }
    if (data.to_department_id) {
      payload.to_department_id = data.to_department_id;
    }
    if (data.tag_id) {
      payload.tag_id = data.tag_id;
    }
    if (data.total_estimated_value) {
      payload.total_estimated_value = data.total_estimated_value;
    }
    if (data.total_price) {
      payload.total_price = data.total_price;
    }
    await itemRequestsApi.mrAction(ticketId, payload);
    dispatch(
      showNotification({
        type: "success",
        message: "Action submitted successfully",
      })
    );
    dispatch(getById(ticketId));
  } catch (err) {
    dispatch(
      showNotification({
        type: "error",
        message: err.response?.data?.errors?.[0] || "Failed to submit action",
      })
    );
    throw new Error(err);
  } finally {
    dispatch(setLoading("mrAction"));
  }
};

const stockCheck = (data) => async (dispatch, getState) => {
  try {
    const ticketId = getState().itemRequests.itemRequest_details.data.id;
    dispatch(setLoading("stockCheck"));
    await itemRequestsApi.stockCheck(data);
    dispatch(
      showNotification({
        type: "success",
        message: "Stock check submitted successfully",
      })
    );
    dispatch(setStockCheckDialog());
    dispatch(getById(ticketId));
  } catch (err) {
    dispatch(
      showNotification({
        type: "error",
        message: err.response?.data?.errors?.[0] || "Failed to submit stock check",
      })
    );
    throw new Error(err);
  } finally {
    dispatch(setLoading("stockCheck"));
  }
};

export const ItemRequests = {
  index,
  getById,
  create,
  remove,
  reply,
  removeReply,
  changeReplyPrivacy,
  mrAction,
  stockCheck,
};
